
import { Tooltip } from "antd"
import { InformationCircleIcon } from "@heroicons/react/outline"
import { useTranslation } from "react-i18next"
import i18n from "../../../translations"
import { TrashIcon } from "../../../components/Icons/TrashIcon"
import { AssignOrdersGroupableIndicator } from "./AssignOrdersGroupableIndicator"
import { insertNames } from "./sipStoreBound"

const truncateWithEllipsis = (string, maxLength) => {
  if (string.length <= maxLength) {
    return string
  }
  let truncatedString = string.slice(0, (maxLength))
  truncatedString += "..."
  return truncatedString
}

export const groupOrdersColumns = (groupableCount = 0) => {
  const topAlignedHeader = () => ({ style: { verticalAlign: "top" } })
  return [
    {
      dataIndex: "store",
      title: i18n.t("picking_tasks.assign_columns.store"),
      onHeaderCell: topAlignedHeader,
      render: (store) => <span title={store?.name}>{truncateWithEllipsis(store?.name || "", 16)}</span>,
    },
    {
      dataIndex: "order_number",
      title: i18n.t("picking_tasks.assign_columns.order_number"),
      onHeaderCell: topAlignedHeader,
      render: (order_number) => <>#{order_number}</>,
    },
    {
      dataIndex: "channel_name",
      title: i18n.t("picking_tasks.assign_columns.channel_name"),
      onHeaderCell: topAlignedHeader,
      render: (channel_name) => <>{channel_name || "-"}</>,
    },
    {
      dataIndex: "assigned_stock_quantity",
      title: i18n.t("picking_tasks.assign_columns.pieces"),
      onHeaderCell: topAlignedHeader,
      render: (quantity) => <>{quantity ?? 0}</>,
    },
    {
      dataIndex: "apt_for_single_item_picking",
      onHeaderCell: topAlignedHeader,
      title: (
        <div>
          <div className="flex items-center gap-1">
            <span>{i18n.t("picking_tasks.assign_columns.groupable")}</span>
            <Tooltip title={i18n.t("picking_tasks.assign_columns.groupable_help")}>
              <InformationCircleIcon className="h-4 w-4 text-gray-400 cursor-help" />
            </Tooltip>
          </div>
          <div className="mt-1 text-xs font-normal text-gray-400">{i18n.t("picking_tasks.assign_columns.groupable_count", { n: groupableCount })}</div>
        </div>
      ),
      render: (apt, object) => {
        // The listing only flags eligibility (apt_for_single_item_picking) without a
        // per-order reason, so surface the generic "not eligible" message client-side.
        // Specific conflicts (errors) only arrive after a failed assign attempt.
        const reasons = [...(object.errors || [])]
        if (!apt && !reasons.some(e => e.error_type === "not_apt_for_single_item_picking")) {
          reasons.push({ error_type: "not_apt_for_single_item_picking" })
        }
        return (
          <span className="inline-flex items-center gap-1">
            <span className={apt ? "text-green-600 font-medium" : "text-red-500 font-medium"}>
              {apt ? i18n.t("picking_tasks.assign_columns.yes") : i18n.t("picking_tasks.assign_columns.no")}
            </span>
            {reasons.length > 0 && (
              <AssignOrdersGroupableIndicator groupable={null} errors={reasons} />
            )}
            {apt && object.single_item_picking_store_bound && (
              <Tooltip title={i18n.t("picking_tasks.assign_columns.insert_tooltip", { names: insertNames(object).join(", ") || i18n.t("picking_tasks.assign_columns.insert_fallback") })}>
                <span className="ml-1 rounded bg-yellow-100 px-1.5 py-0.5 text-xs font-medium text-yellow-800">{i18n.t("picking_tasks.assign_columns.insert_badge")}</span>
              </Tooltip>
            )}
          </span>
        )
      },
    },
    {
      dataIndex: "deleteCallback",
      title: "",
      className: "w-0",
      onHeaderCell: topAlignedHeader,
      render: (deleteCallback = () => {}, object) => (
        <div onClick={() => { deleteCallback(object.id) }} className="text-center cursor-pointer rounded-md hover:bg-gray-200 p-1">
          <TrashIcon className="w-4 h-4 text-gray-500 inline" />
        </div>
      ),
    },
  ]
}

export const ordersLeftTableColumns = [
  {
    dataIndex: "store",
    title: "Tienda",
    render: (store) => <span title={store.name}>{truncateWithEllipsis(store.name, 10)}</span>,
  },
  {
    dataIndex: "order_number",
    title: "Núm de Pedido",
    render: (order_number) => <>#{order_number}</>,
  },
  {
    dataIndex: "apt_for_single_item_picking",
    title: "Agrupable",
    className: "w-0",
    render: (sip, object) => (
      <AssignOrdersGroupableIndicator groupable={sip} errors={object.errors}/>
    ),
  },
  {
    dataIndex: "deleteCallback",
    title: "",
    className: "w-0",
    render: (deleteCallback = () => {}, object) => (
      <div onClick={() => {deleteCallback(object.id)}} className="text-center w-100 cursor-pointer rounded-md hover:bg-gray-200 p-1">
        <TrashIcon className="w-4 h-4 text-red-700 inline" />
      </div>
    ),
  },
]

export const ordersRightTableColumns = [
  {
    dataIndex: "store",
    title: "Tienda",
    render: (store) => <span title={store.name}>{truncateWithEllipsis(store.name, 10)}</span>,
  },
  {
    dataIndex: "order_number",
    title: "Núm de Pedido",
    render: (order_number) => <>#{order_number}</>,
  },
  {
    dataIndex: "id",
    title: "",
    render: (id, object) => (
      <AssignOrdersGroupableIndicator groupable={null} errors={object.errors}/>
    ),
  },
  {
    dataIndex: "deleteCallback",
    title: "",
    className: "w-0",
    render: (deleteCallback = () => {}, object) => (
      <div onClick={() => {deleteCallback(object.id)}} className="text-center w-100 cursor-pointer rounded-md hover:bg-gray-200 p-1">
        <TrashIcon className="w-4 h-4 text-red-700 inline" />
      </div>
    ),
  },
]

// pickerColumns is a module-level constant, so its headers hold keys and resolve them at render.
const ColumnHeader = ({ titleKey, subtitleKey }) => {
  const { t } = useTranslation()
  return (
    <div className="leading-tight">
      <div className="font-medium text-gray-700">{t(titleKey)}</div>
      <div className="text-xs font-normal text-gray-400">{t(subtitleKey)}</div>
    </div>
  )
}

const columnHeader = (titleKey, subtitleKey) => <ColumnHeader titleKey={titleKey} subtitleKey={subtitleKey} />

const pickingOrdersCell = (pickingOrders = []) => {
  const grouped = pickingOrders.filter(po => po.single_item_picking === true).length
  const nonGrouped = pickingOrders.filter(po => po.single_item_picking === false).length
  return (
    <div className="leading-tight">
      <div className="text-base text-gray-700">{pickingOrders.length}</div>
      <div className="text-xs text-gray-400">{i18n.t("picking_tasks.assign_columns.picking_orders_split", { nonGrouped, grouped })}</div>
    </div>
  )
}

export const pickerColumns = [
  {
    title: columnHeader("picking_tasks.assign_columns.picker_name", "picking_tasks.assign_columns.email"),
    dataIndex: "first_name",
    render: (firstName, object) => (
      <div className="leading-tight">
        <div className="text-gray-700">{firstName} {object.last_name}</div>
        {object.email && (
          <div className="text-xs text-gray-400 truncate max-w-[220px]" title={object.email}>{object.email}</div>
        )}
      </div>
    ),
  },
  {
    title: columnHeader("picking_tasks.assign_columns.queued_orders", "picking_tasks.assign_columns.grouping_split"),
    dataIndex: "new_picking_orders",
    render: (newPickingOrders) => pickingOrdersCell(newPickingOrders),
  },
  {
    title: columnHeader("picking_tasks.assign_columns.in_progress_orders", "picking_tasks.assign_columns.grouping_split"),
    dataIndex: "incomplete_picking_orders",
    render: (incompletePickingOrders) => pickingOrdersCell(incompletePickingOrders),
  },
]
